<script lang="ts">
  import LogoIcon from "../utils/assets/logo.svelte";
  import CrossIcon from "../utils/assets/cross_icon.svelte";
  import ChatBubbleIcon from "../utils/assets/chat_bubble_icon.svelte";
  import { isOpen, hasOpened, toggleWidget } from "../stores/widget";
  import { pendingMessages } from "../stores/chat";
  import BubbleArrow from "../utils/assets/bubble_arrow.svelte";
  import { client_config } from "../stores/store";
  import { client_id } from "../stores/chat";
  import {
    WIDGET_BOTTOM_SPACE,
    CLIENT_LOGO_OVERRIDE_STYLES,
  } from "../utils/constants";
  import { resolveButtonSizes, launcherSizeVars } from "../utils/buttonSizing";

  $: buttonSizes = resolveButtonSizes($client_config);
  $: launcherVars = launcherSizeVars(buttonSizes.launcher);

  let popupMessageOption = $client_config?.popup_message_position || "left";
  let isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;

  $: logoStyles =
    CLIENT_LOGO_OVERRIDE_STYLES[
      $client_id as keyof typeof CLIENT_LOGO_OVERRIDE_STYLES
    ]?.[isMobile ? "mobile" : "desktop"];

  $: if (logoStyles) {
    document?.documentElement?.style?.setProperty(
      "--client-logo-width",
      "auto"
    );
    document?.documentElement?.style?.setProperty(
      "--client-logo-height",
      logoStyles.height || (isMobile ? "40px" : "45px")
    );
    document?.documentElement?.style?.setProperty(
      "--client-logo-min-width",
      logoStyles.minWidth || (isMobile ? "40px" : "45px")
    );
    document?.documentElement?.style?.setProperty(
      "--client-logo-min-height",
      logoStyles.minHeight || (isMobile ? "40px" : "45px")
    );
    document?.documentElement?.style?.setProperty(
      "--client-logo-border-radius",
      logoStyles.borderRadius || "50%"
    );
  }

  let topPosition = popupMessageOption === "top";

  $: document?.documentElement?.style?.setProperty(
    "--margin-bottom",
    topPosition ? "92.5px" : "80px"
  );
  $: document?.documentElement?.style?.setProperty(
    "--margin-right",
    topPosition ? "-8px" : "82px"
  );
  $: document?.documentElement?.style?.setProperty(
    "--bubble-radius",
    topPosition ? "8px" : isLeftPositioned ? "8px 8px 8px 0px" : "8px 8px 0px 8px"
  );

  export let isLeftPositioned: boolean = false;
  export let verticalOffset: string = "16px";

  $: bottomSpacing = isMobile
    ? WIDGET_BOTTOM_SPACE[$client_id as keyof typeof WIDGET_BOTTOM_SPACE]
        ?.mobile || "16px"
    : verticalOffset;
</script>

<button
  aria-haspopup="dialog"
  aria-expanded="false"
  data-state="closed"
  on:click={toggleWidget}
  class="verbaflo-widget-btn"
  class:verbaflo-widget-btn-left={isLeftPositioned}
  style={`bottom: ${bottomSpacing} !important; ${launcherVars}`}
>
  {#if $isOpen}
    <CrossIcon />
  {:else}
    {#if $client_config?.client_logo_url_override}
      <img
        src={$client_config?.client_logo_url_override}
        alt="logo"
        class="verbaflo-widget-client-logo-override"
      />
    {:else}
      <span class="verbaflo-widget-launcher-logo">
        <LogoIcon />
      </span>
    {/if}
    {#if $pendingMessages?.length > 0 || !$hasOpened}
      <div class="verbaflo-widget-btn-dot" />
    {/if}
    {#if !$hasOpened && $client_config?.popup_message}
      <div
        class="verbaflo-widget-bubble-container {topPosition
          ? 'top-position'
          : 'left-position'}"
      >
        <ChatBubbleIcon size={topPosition ? "15" : "16"} />
        <p
          class="verbaflo-widget-bubble-popup-message {topPosition &&
            'top-position'}"
        >
          {$client_config?.popup_message}
        </p>
        {#if topPosition}
          <div class="bubble-arrow-container">
            <BubbleArrow />
          </div>
        {/if}
      </div>
    {/if}
  {/if}
</button>

<style>
  :global(#vfwidget .verbaflo-widget-btn) {
    position: fixed !important;
    right: var(--vf-h-offset, 16px) !important;
    width: var(--vf-launcher-size-d, 80px) !important;
    height: var(--vf-launcher-size-d, 80px) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    margin: 0 !important;
    border: 1px solid #14161b !important;
    border-radius: var(--vf-launcher-radius, 50%) !important;
    background-color: #14161b !important;
    color: #ffffff !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 20px !important;
    text-transform: none !important;
    cursor: pointer !important;
    opacity: 1 !important;
    transition: transform 0.2s ease-in-out !important;
  }

  :global(#vfwidget .verbaflo-widget-launcher-logo) {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-launcher-logo > svg) {
    height: calc(
      var(--vf-launcher-size-d, 80px) * var(--vf-launcher-icon-ratio, 0.75)
    ) !important;
    width: auto !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-btn:hover) {
    transform: scale(1.05) !important;
    color: #1f2937 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-btn:active) {
    transform: scale(1.05) !important;
    color: #1f2937 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-btn:disabled) {
    pointer-events: none !important;
  }

  :global(#vfwidget .verbaflo-widget-btn.verbaflo-widget-btn-left) {
    right: auto !important;
    left: var(--vf-h-offset, 16px) !important;
  }

  :global(#vfwidget .verbaflo-widget-btn-dot) {
    position: absolute !important;
    top: 4px !important;
    right: 4px !important;
    width: 14px !important;
    height: 14px !important;
    background-color: red !important;
    border-radius: 50% !important;
  }

  :global(#vfwidget .verbaflo-widget-bubble-container) {
    position: absolute !important;
    right: 0 !important;
    bottom: 0 !important;
    margin-bottom: var(--margin-bottom) !important;
    margin-right: var(--margin-right) !important;
    min-width: max-content !important;
    display: flex !important;
    align-items: center !important;
    column-gap: 10px !important;
    padding: 6px 10px !important;
    border: 1px solid black !important;
    border-radius: var(--bubble-radius) !important;
    background-color: white !important;
    color: #051133 !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-bubble-popup-message) {
    margin: 0 !important;
    padding: 0 !important;
    color: #051133 !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: normal !important;
    max-width: 300px !important;
  }

  :global(#vfwidget .bubble-arrow-container) {
    position: absolute !important;
    bottom: -19px !important;
    right: 38px !important;
  }

  :global(#vfwidget .verbaflo-widget-btn-left .verbaflo-widget-btn-dot) {
    right: auto !important;
    left: 4px !important;
  }

  :global(
      #vfwidget .verbaflo-widget-btn-left .verbaflo-widget-bubble-container
    ) {
    right: auto !important;
    left: 0 !important;
    margin-right: 0 !important;
    margin-left: var(--margin-right) !important;
  }

  :global(#vfwidget .verbaflo-widget-btn-left .bubble-arrow-container) {
    right: auto !important;
    left: 38px !important;
  }

  :global(#vfwidget .verbaflo-widget-client-logo-override) {
    width: var(--client-logo-width, 45px) !important;
    height: var(--client-logo-height, 45px) !important;
    min-width: var(--client-logo-min-width, 45px) !important;
    min-height: var(--client-logo-min-height, 45px) !important;
    object-fit: cover !important;
    border-radius: var(--client-logo-border-radius, 50%) !important;
    flex-shrink: 0 !important;
  }

  @media only screen and (max-width: 430px) {
    :global(#vfwidget .verbaflo-widget-btn) {
      width: var(--vf-launcher-size-m, 70px) !important;
      height: var(--vf-launcher-size-m, 70px) !important;
    }

    :global(#vfwidget .verbaflo-widget-launcher-logo > svg) {
      height: calc(
        var(--vf-launcher-size-m, 70px) * var(--vf-launcher-icon-ratio, 0.75)
      ) !important;
    }

    :global(#vfwidget .verbaflo-widget-bubble-container.top-position) {
      margin-bottom: calc(var(--margin-bottom) - 10px) !important;
      padding: 6px 8px !important;
      column-gap: 6px !important;
    }

    :global(#vfwidget .verbaflo-widget-bubble-container.left-position) {
      margin-bottom: calc(var(--margin-bottom) - 6px) !important;
      margin-right: calc(var(--margin-right) - 11px) !important;
    }

    :global(
        #vfwidget
          .verbaflo-widget-btn-left
          .verbaflo-widget-bubble-container.left-position
      ) {
      margin-right: 0 !important;
      margin-left: calc(var(--margin-right) - 11px) !important;
    }

    :global(#vfwidget .verbaflo-widget-bubble-popup-message.top-position) {
      font-size: 13px !important;
    }

    :global(#vfwidget .bubble-arrow-container) {
      right: 32px !important;
    }

    :global(#vfwidget .verbaflo-widget-btn-left .bubble-arrow-container) {
      right: auto !important;
      left: 32px !important;
    }

    :global(#vfwidget .verbaflo-widget-client-logo-override) {
      width: var(--client-logo-width, 40px) !important;
      height: var(--client-logo-height, 40px) !important;
      min-width: var(--client-logo-min-width, 40px) !important;
      min-height: var(--client-logo-min-height, 40px) !important;
    }
  }
</style>
